<script setup lang="ts">
import { useAppStore } from '@/core/collections/app.store';
import { JOB_BOARD_ENDPOINTS } from '@/core/collections/job-boards/job-board.endpoints';
import { JobBoard } from '@/core/collections/job-boards/job-board.interface';
import { ApiResource, NxtGenericList, NxtIconButton, NxtToggle } from '@future-harry/nxt-styles';
import dayjs from 'dayjs';
import JobBoardModal from '@/apps/admin/components/modals/JobBoardModal.vue';
import ConfirmationModal from '@/core/components/layout-elements/ConfirmationModal.vue';
import { notifyError } from '@/core/helpers/notification.helper';
import { useDelete } from '@/core/hooks/useFetch';
import { createJobBoard, fetchJobBoards } from '@/core/collections/job-boards/job-board.service';
import { useRouter } from 'vue-router';
import { useUserInfoStore } from '@/core/collections/user-info/user-info.store';
import { storeToRefs } from 'pinia';
import { useSettingsStore } from '@/apps/admin/stores/settings.store';
import { SettingsTabId } from '@/apps/admin/components/settings/constants';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const { t } = useI18n();
const router = useRouter();
const { userInfo } = storeToRefs(useUserInfoStore());

const jobBoardOverview = ref<ApiResource<Array<JobBoard>> | null>(null);
const isLoading = ref(false);
const showDeleteConfirmation = ref(false);
const jobBoardToDelete = ref<JobBoard | null>(null);
const isDeletingJobBoard = ref(false);

onMounted(async () => {
   appStore.setBreadcrumbs([
      {
         content: 'Jobsites',
         to: '/',
      },
   ]);

   await loadJobBoards(1);
});

const loadJobBoards = async (page: number) => {
   try {
      isLoading.value = true;
      jobBoardOverview.value = await fetchJobBoards(page);
   } catch (error) {
      notifyError({ title: t('errors.error') });
   } finally {
      isLoading.value = false;
   }
};

const handleCreateJobBoard = async (jobBoard: JobBoard) => {
   try {
      const response = await createJobBoard(jobBoard, userInfo.value?.tenant?.id as number);
      router.push(`/${response.id}`);
   } catch (error: any) {
      if (error?.response?.data?.errors) {
         const transformedErrors: Record<string, string> = {};

         // transform errors to single string values, url field can throw error from backend (duplicate entry)
         Object.keys(error.response.data.errors).forEach((key) => {
            const errorValue = error.response.data.errors[key];
            transformedErrors[key] = Array.isArray(errorValue) ? errorValue[0] : errorValue;
         });

         settingsStore.setValidationErrors(ref(transformedErrors));
         settingsStore.setActiveTab(SettingsTabId.General);
      }

      notifyError({ title: t('job-board.settings.save-error') });
   }
};

const handleDelete = async (jobBoard: JobBoard) => {
   try {
      await useDelete<ApiResource<JobBoard>>(JOB_BOARD_ENDPOINTS.DELETE(jobBoard.id));
      // remove/update from ui
      if (!jobBoardOverview.value) return;
      jobBoardOverview.value.data = jobBoardOverview.value.data.filter((jb: JobBoard) => jb.id !== jobBoard.id);
   } catch (error) {
      notifyError({ title: t('errors.error') });
   }
};

const handleDeleteClick = (jobBoard: JobBoard) => {
   jobBoardToDelete.value = jobBoard;
   showDeleteConfirmation.value = true;
};

const handleConfirmDelete = async () => {
   if (!jobBoardToDelete.value) return;
   isDeletingJobBoard.value = true;
   try {
      await handleDelete(jobBoardToDelete.value);
      await loadJobBoards(1);
   } finally {
      isDeletingJobBoard.value = false;
      showDeleteConfirmation.value = false;
      jobBoardToDelete.value = null;
   }
};

const handleCancelDelete = () => {
   showDeleteConfirmation.value = false;
   jobBoardToDelete.value = null;
};

const handlePageSet = (page: number) => {
   loadJobBoards(page);
};
</script>

<template>
   <div class="jobboard-overview">
      <Teleport to=".page-header__bottom">
         {{ t('jobsites.jobsites-description') }}
      </Teleport>
      <NxtGenericList
         :data="(jobBoardOverview?.data ?? []) as JobBoard[]"
         :meta="jobBoardOverview?.meta"
         :is-loading="isLoading"
         :loading-col-count="5"
         :loading-row-count="10"
         :on-set-page="handlePageSet"
      >
         <template #headings>
            <th>{{ $t('jobsites.jobsites') }}</th>
            <th>{{ $t('companies.companies') }}</th>
            <th>{{ $t('jobsites.update') }}</th>
            <th>{{ $t('jobsites.status') }}</th>
            <th></th>
         </template>
         <template #list-item="{ listItem: jobBoard }">
            <td>
               <RouterLink :to="`/${jobBoard.id}`">{{ jobBoard.name }}</RouterLink>
            </td>
            <td>{{ jobBoard.tenant?.name }}</td>
            <td class="jobboard-overview__last-update">
               {{ `${t('generic.last-updated-on')} ${dayjs(jobBoard.updated_at).format('DD/MM/YYYY')}` }}
            </td>
            <td>
               <NxtToggle
                  disabled
                  :model-value="jobBoard.is_published"
                  :name="`job-board-status-${jobBoard.id}`"
                  :label="jobBoard.is_published ? t('jobsites.online') : t('jobsites.offline')"
               />
            </td>
            <td align="right"><NxtIconButton name="trash" @on-click="() => handleDeleteClick(jobBoard)" /></td>
         </template>
         <template #create-modal="{ closeCreateModal }">
            <JobBoardModal @on-create="handleCreateJobBoard" @on-close="closeCreateModal" />
         </template>
      </NxtGenericList>
      <ConfirmationModal
         v-if="showDeleteConfirmation && jobBoardToDelete"
         :title="t('confirmation.delete-title', { type: 'jobsite' })"
         :message="t('confirmation.delete-message', { type: 'jobsite' })"
         :confirm-text="t('actions.delete')"
         :cancel-text="t('actions.cancel')"
         :is-loading="isDeletingJobBoard"
         variant="danger"
         @on-confirm="handleConfirmDelete"
         @on-cancel="handleCancelDelete"
      />
   </div>
</template>

<style lang="scss" scoped>
.jobboard-overview {
   height: 100%;
   width: 100%;
   padding-bottom: var(--nxt-gutter);
   :deep(th) {
      background-color: var(--nxt-extralight-grey);
   }
   :deep(td) {
      background-color: var(--nxt-white);
   }
}

.jobboard-overview__last-update {
   color: var(--nxt-dark-grey);
}
</style>
